import { useState, useEffect } from 'react';
import { getFranchiseUsers } from '../../../utils/api';
import { useData } from '../../../hooks/useData';
import { useAuth } from '../../../hooks/useAuth';
import DefaultLocationSection from './sections/DefaultLocationSection';
import NotificationConfiguration from '@/pages/dashboard/settings/admin/franchise/NotificationConfiguration';
import StandardButton from '../../buttons/StandardButton';
import { type UserProfileV3 } from '@/types/UserProfileV3';

export default function AccountPanel(): JSX.Element {
  const [userProfile, setUserProfile] = useState<UserProfileV3 | null>(null);
  const [defaultLocation, setDefaultLocation] = useState<UserProfileV3['locations'][0] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { profile, selectedFranchise, setFranchiseLocations } = useData();
  const { signOut } = useAuth();

  // 1) Sync React state with "profile" from props/context.
  useEffect(() => {
    if (!profile) return;

    // Update local userProfile state
    setUserProfile(profile);

    // Determine default location from localStorage
    const storedDefaultLocation = localStorage.getItem('defaultLocation');
    const matchingLocation = profile.locations.find(
      (location) => location.id === storedDefaultLocation
    );

    if (matchingLocation) {
      setDefaultLocation(matchingLocation);
    } else {
      // If no default found in localStorage, you can pick the first one or leave undefined
      setDefaultLocation(profile.locations[0]);
    }
  }, [profile]);

  useEffect(() => {
    getFranchiseUsers(selectedFranchise?.id ?? '')
      .then((data) => {
        setFranchiseLocations(data.franchiseLocations);
      })
      .catch((error) => {
        console.error(
          `Error fetching users for franchise ${selectedFranchise?.id}:`,
          error
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedFranchise]);

  // Handle selecting a new default location.
  function handleSelectDefaultLocation(id: string): void {
    if (!userProfile) return;

    const newDefaultLocation = userProfile.locations.find(
      (location) => location.id === id
    );
    if (newDefaultLocation) {
      localStorage.setItem('defaultLocation', newDefaultLocation.id);
      setDefaultLocation(newDefaultLocation);
    }
  }

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
          My Account
        </h2>

        {/* Set Default Location */}
        {userProfile && defaultLocation && (
          <DefaultLocationSection
            userProfile={userProfile}
            selectedLocation={defaultLocation}
            handleChange={handleSelectDefaultLocation}
          />
        )}

        {/* Location Notifications */}
        {!isLoading && userProfile && <NotificationConfiguration/>}

        {/* Sign Out */}
        <StandardButton
          onClick={() => {
            signOut().catch(console.error);
          }}
          dataCy="sign-out-button"
        >
          Sign Out
        </StandardButton>
      </div>
    </div>
  );
}
